import type { RouteLocationNormalized, RouteLocationRaw, RouteParams } from "vue-router";

import type { DeveloperNotificationException } from "@core/core/exceptions/DeveloperNotificationException";
import { BenutzerKompetenz } from "@core/core/types/benutzer/BenutzerKompetenz";
import { ServerMode } from "@core/core/types/ServerMode";
import type { SchuelerLaufbahnplanungProps } from "@ui/components/gost/laufbahnplanung/SSchuelerLaufbahnplanungProps";
import { ConfigElement } from "@ui/utils/Config";

import { type RouteSchueler, routeSchueler } from "~/router/apps/schueler/RouteSchueler";
import { routeError } from "~/router/error/RouteError";
import { schulformenGymOb } from "~/router/RouteHelper";
import { RouteNode } from "~/router/RouteNode";
import { benutzerStateImpl } from "~/states/BenutzerStateImpl";
import { configStateImpl } from "~/states/ConfigStateImpl";
import { gostLaufbahnplanungStateImpl } from "~/states/GostLaufbahnplanungStateImpl";
import { useSchuelerAuswahlState } from "~/states/schueler/SchuelerAuswahlState";

const SSchuelerLaufbahnplanung = () => import("@ui/components/gost/laufbahnplanung/SSchuelerLaufbahnplanung.vue");

export class RouteSchuelerLaufbahnplanung extends RouteNode<any, RouteSchueler> {

	public constructor() {
		super(schulformenGymOb, [
			BenutzerKompetenz.OBERSTUFE_LAUFBAHNPLANUNG_ALLGEMEIN,
			BenutzerKompetenz.OBERSTUFE_LAUFBAHNPLANUNG_FUNKTIONSBEZOGEN,
		], "schueler.laufbahnplanung", "laufbahnplanung", SSchuelerLaufbahnplanung);
		super.mode = ServerMode.STABLE;
		super.propHandler = (route) => this.getProps(route);
		super.text = "Laufbahnplanung";
		this.isHidden = (params?: RouteParams) => {
			return this.checkHidden(params);
		};
		configStateImpl.config.addElements([
			new ConfigElement("app.gost.belegpruefungsart", "user", "gesamt"),
			new ConfigElement("app.schueler.laufbahnplanung.modus", "user", "normal"),
			new ConfigElement("app.schueler.laufbahnplanung.faecher.anzeigen", "user", "alle"),
		]);
	}

	protected checkHidden(params?: RouteParams) {
		try {
			const { id } = (params === undefined) ? { id: undefined } : RouteNode.getIntParams(params, ["id"]);
			const schuelerAuswahlState = useSchuelerAuswahlState();
			if (!schuelerAuswahlState.manager.hasDaten()) {
				return false;
			}
			const abiturjahr = schuelerAuswahlState.manager.auswahl().abiturjahrgang;
			// Bis zum Wechsel gehört ein Schüler mit Wiederholung zu beiden Jahrgängen, daher genügt die Kompetenz für einen davon
			const abiturjahrWiederholung = schuelerAuswahlState.manager.auswahl().abiturjahrgangWiederholung;
			if (((abiturjahr !== null) && schuelerAuswahlState.manager.abiturjahrgaenge.get(abiturjahr))
				&& (benutzerStateImpl.benutzerHatKompetenz(BenutzerKompetenz.OBERSTUFE_LAUFBAHNPLANUNG_ALLGEMEIN)
					|| (benutzerStateImpl.benutzerHatKompetenz(BenutzerKompetenz.OBERSTUFE_LAUFBAHNPLANUNG_FUNKTIONSBEZOGEN)
						&& (benutzerStateImpl.kompetenzenAbiturjahrgaenge.has(abiturjahr)
							|| ((abiturjahrWiederholung !== null) && benutzerStateImpl.kompetenzenAbiturjahrgaenge.has(abiturjahrWiederholung)))))) {
				return false;
			}
			return routeSchueler.getRouteDefaultChild({ id });
		} catch (e) {
			return routeError.getSimpleErrorRoute(e as DeveloperNotificationException);
		}
	}

	protected async update(to: RouteNode<any, any>, to_params: RouteParams, from: RouteNode<any, any> | undefined, from_params: RouteParams, isEntering: boolean): Promise<void | Error | RouteLocationRaw> {
		try {
			const schuelerAuswahlState = useSchuelerAuswahlState();
			if (isEntering) {
				// Wenn man in die Laufbahnplanung wechselt und von einer Gost-Route per Schülerlink kommt, dann im Filter direkt den Jahrgang wählen
				if ((from !== undefined) && from.checkSuccessorOf('gost') !== false) {
					for (const e of schuelerAuswahlState.manager.jahrgaenge.list()) {
						if (e.id === schuelerAuswahlState.manager.auswahl().idJahrgang) {
							schuelerAuswahlState.manager.jahrgaenge.auswahlAdd(e);
							await schuelerAuswahlState.setFilter();
							break;
						}
					}
				}
			}
			const { id } = RouteNode.getIntParams(to_params, ["id"]);
			if (id === undefined) {
				await gostLaufbahnplanungStateImpl.ladeSchuelerDaten(null);
				return;
			}
			try {
				await gostLaufbahnplanungStateImpl.ladeSchuelerDaten(schuelerAuswahlState.manager.liste.get(id));
			} catch {
				return routeSchueler.getRoute({ id });
			}
		} catch (e) {
			return await routeError.getErrorRoute(e as DeveloperNotificationException);
		}
	}

	public async leaveBefore(from: RouteNode<any, any>, from_params: RouteParams): Promise<void> {
		await gostLaufbahnplanungStateImpl.clear();
	}

	public getProps(to: RouteLocationNormalized): SchuelerLaufbahnplanungProps {
		return { };
	}

}

export const routeSchuelerLaufbahnplanung = new RouteSchuelerLaufbahnplanung();
